import type { DozerConfig } from '../config/dozer.config';
import type { PlayerConfig } from '../config/player.config';
import type { WorldConfig } from '../config/world.config';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import type { SegmentInstance } from '../level/segments/SegmentInstance';
import type { ObstacleManager } from '../obstacles/ObstacleManager';
import { aabb, intersects } from '../physics/Aabb';
import type { PlayerController } from '../player/PlayerController';
import { PlayerState } from '../player/PlayerState';
import type { VehicleSeat } from '../player/VehicleSeat';
import type { ScoreManager } from '../scoring/ScoreManager';
import { clamp } from '../utils/math';
import { catapultSpeed, dozerParts, dozerSeat, dozerSpan, finishHopSpeed, mountMinFeet } from './dozerRules';
import { DozerExit, DozerPhase, DozerRide } from './DozerRide';

export interface DozerManagerDeps {
  readonly player: PlayerController;
  readonly obstacles: ObstacleManager;
  readonly score: ScoreManager;
  readonly events: EventBus<GameEvents>;
  readonly config: DozerConfig;
  readonly playerConfig: PlayerConfig;
  readonly world: WorldConfig;
  /** Run speed at a track distance (difficulty) – the catapult over the pit is timed for it. */
  readonly speedAt: (distance: number) => number;
  /** Fixed simulation step (s) – the catapult accounts for its integration (physics/JumpReach.ts). */
  readonly fixedStep: number;
  /**
   * The runner is protected (invulnerable after a hit, debug god mode): like the vault over a wall, it gets on the
   * dozer from the ground too – smashing through a machine would end inside it. Default: never.
   */
  readonly isProtected?: () => boolean;
}

/** Terrain id of the earth the pile leaves in the pit. */
const FILL_ID = 'SURF_DOZER_FILL';
const EPSILON = 1e-6;

/**
 * The dozer set piece (SEG_DOZER_60M) in play – pure gameplay, stepped after the player and before the RunDirector:
 *
 *   parked     the dozer is an obstacle of the module (OBS_DOZER_A). A runner whose feet are within a step of the
 *              track top when it touches the dozer (a jump onto track, fender or seat) gets on: the obstacle leaves
 *              play before the RunDirector sees the contact, the PlayerController takes the seat (DRIVING). Lower –
 *              on foot – the RunDirector's rules apply: a frontal hit, a stumble from the side.
 *   driving    the dozer follows its rider. Heaps (DozerConfig.heapObstacleId) its body meets are pushed: out of play,
 *              onto the pile, +bottles. Everything else is an obstacle of the RunDirector – a hit throws the rider off.
 *   pit        enough earth: the pile fills the pit in the dozer's lane (the segment's terrain changes), +bottles;
 *              every heap pushed: +extra life (score:extraLife – the drink break follows). The ride ends over the
 *              filled pit with a jump off. Too little earth: the dozer stops at the edge, the rider is catapulted over
 *              the pit – never a fall, never a lost life.
 *   abandoned  the rider is off (jump key, hit, end, catapult): the dozer brakes to a stop – never into the pit.
 */
export class DozerManager {
  private readonly rides = new Map<number, DozerRide>();
  private readonly seat: VehicleSeat;
  private readonly minFeet: number;
  private readonly span: { readonly rear: number; readonly front: number };
  private readonly body = aabb();

  constructor(private readonly deps: DozerManagerDeps) {
    this.seat = dozerSeat(deps.config, deps.playerConfig);
    this.minFeet = mountMinFeet(deps.config, deps.playerConfig);
    this.span = dozerSpan(deps.config.vehicle.colliders);
    // Subscribed after the ObstacleManager: the module's obstacles exist when its segment spawns.
    deps.events.on('segment:spawned', ({ segment }) => this.spawn(segment));
    deps.events.on('segment:recycled', ({ segment }) => this.rides.delete(segment.uid));
  }

  /** Every dozer of the spawned modules. */
  all(): readonly DozerRide[] {
    return [...this.rides.values()];
  }

  /** The dozer the runner drives, or null. */
  get active(): DozerRide | null {
    for (const ride of this.rides.values()) if (ride.phase === DozerPhase.Driving) return ride;
    return null;
  }

  /** The seat the PlayerController rides (all dozers alike). */
  get vehicleSeat(): VehicleSeat {
    return this.seat;
  }

  /** Fixed step, after the player moved and before the RunDirector checks its contacts. */
  step(dt: number): void {
    for (const ride of this.rides.values()) {
      if (!ride.segment.committed) continue;
      ride.keepPrevious();
      switch (ride.phase) {
        case DozerPhase.Parked:
          this.tryMount(ride);
          break;
        case DozerPhase.Driving:
          this.drive(ride);
          break;
        case DozerPhase.Abandoned:
          this.brake(ride, dt);
          break;
      }
    }
  }

  reset(): void {
    this.rides.clear();
  }

  private spawn(segment: SegmentInstance): void {
    const setPiece = segment.definition.dozer;
    if (!setPiece) return;
    const { config, obstacles, world } = this.deps;
    const placed = obstacles.forSegment(segment.uid);
    const dozer = placed.find((o) => o.spawnPoint.id === setPiece.spawnPointId && o.definition.id === config.vehicle.obstacleId);
    if (!dozer) return; // every layout places it (segment validator) – nothing to ride otherwise
    const heaps = placed.filter((o) => o.definition.id === config.heapObstacleId);
    this.rides.set(segment.uid, new DozerRide(segment, dozer, heaps, dozerParts(segment.definition).pit ?? null, dozer.spawnPoint.lane, world.groundHeight));
  }

  // ── Parked ───────────────────────────────────────────────────────────────

  private tryMount(ride: DozerRide): void {
    const { player, obstacles, events } = this.deps;
    if (!ride.obstacle.active || player.vehicle || player.isIncapacitated) return; // crashed into (cleared), or someone rides already
    const jumpedOn = player.isAirborne && player.current.y >= this.minFeet - EPSILON;
    if (!jumpedOn && !(this.deps.isProtected?.() ?? false)) return;
    player.colliderOf('current', this.body);
    if (!ride.obstacle.trackColliders.some((c) => intersects(this.body, c))) return;
    player.mount(this.seat, { distance: ride.distance + this.deps.config.vehicle.seat.z, lane: ride.lane, ground: ride.ground });
    if (player.vehicle !== this.seat) return;
    obstacles.retire(ride.obstacle); // before the RunDirector sees the contact – the dozer is the rider's now
    ride.phase = DozerPhase.Driving;
    events.emit('dozer:mounted', { ride });
  }

  // ── Driving ──────────────────────────────────────────────────────────────

  private drive(ride: DozerRide): void {
    const { player, config } = this.deps;
    if (player.vehicle !== this.seat || player.state !== PlayerState.Driving) {
      // A hit stopped the machine dead (the rider was thrown over the blade); a jump off lets it brake.
      const thrown = player.state === PlayerState.Hit || player.state === PlayerState.Dead;
      this.abandon(ride, thrown ? DozerExit.Thrown : DozerExit.Jumped, thrown);
      return;
    }
    const seatDistance = player.current.distance;
    ride.pose.z = seatDistance - ride.segment.startDistance - config.vehicle.seat.z;
    ride.pose.x = player.current.x;
    ride.pose.speed = player.speed;
    ride.pose.travelled += ride.pose.z - ride.previous.z;

    this.pushHeaps(ride);
    const blade = seatDistance + this.seat.body.front;
    const pit = ride.pit;
    const start = ride.segment.startDistance;
    if (pit && ride.pitFilled === null && blade >= start + pit.zFrom - config.pit.stopMargin) {
      this.reachPit(ride, seatDistance);
      if (ride.phase !== DozerPhase.Driving) return;
    }
    // The seat past the filled pit – or the blade at the end of the module whatever happened: the rider hops off.
    const overPit = pit !== null && ride.pitFilled === true && seatDistance >= start + pit.zTo;
    if (overPit || blade >= ride.segment.endDistance) this.finish(ride, seatDistance);
  }

  /** End of the ride: a hop forward that lands on the module's own run-out (DozerConfig.finish). */
  private finish(ride: DozerRide, seatDistance: number): void {
    const { player, config } = this.deps;
    const landing = ride.segment.endDistance - config.finish.landBeforeEnd;
    player.dismount(finishHopSpeed(player.jumpPhysics, this.seat.seatHeight, landing - seatDistance, this.deps.speedAt(seatDistance), this.deps.fixedStep));
    this.abandon(ride, DozerExit.End);
  }

  /** Heaps the dozer's body meets: out of play onto the pile, bottles for the rider. */
  private pushHeaps(ride: DozerRide): void {
    const { player, obstacles, events, config } = this.deps;
    player.colliderOf('current', this.body);
    for (const heap of ride.heaps) {
      if (!heap.active || !heap.trackColliders.some((c) => intersects(this.body, c))) continue;
      obstacles.clearObstacle(heap);
      ride.heapsPushed++;
      const bottles = config.rewards.bottlesPerHeap;
      this.credit(bottles);
      events.emit('dozer:heapPushed', { ride, heap, heaps: ride.heapsPushed, bottles });
    }
  }

  /** The blade at the pit: fill it with the pile, or – too little earth – stop and catapult the rider over it. */
  private reachPit(ride: DozerRide, seatDistance: number): void {
    const { player, score, events, config, world } = this.deps;
    const pit = ride.pit;
    if (!pit) return;
    if (ride.heapsPushed >= config.pit.fillThreshold) {
      ride.pitFilled = true;
      // The whole pit: the rider may steer (or hop off) anywhere over it – never into an open part.
      ride.segment.fill(FILL_ID, { xFrom: pit.xFrom, xTo: pit.xTo, zFrom: pit.zFrom, zTo: pit.zTo }, world.groundHeight);
      const bottles = config.rewards.pitFilledBottles;
      this.credit(bottles);
      events.emit('dozer:pitReached', { ride, filled: true, bottles });
      if (ride.perfect) {
        let gained = 0;
        for (let i = 0; i < config.rewards.perfectExtraLives; i++) if (score.addExtraLife()) gained++;
        events.emit('dozer:perfect', { ride, lifeGained: gained > 0 });
        if (gained > 0) events.emit('score:extraLife', { extraLives: score.snapshot().extraLives });
      }
      return;
    }
    ride.pitFilled = false;
    // The blade stops at the edge (one fixed step may have carried it past the stop line).
    ride.pose.z = Math.min(ride.pose.z, pit.zFrom - config.pit.stopMargin - this.span.front);
    // Over the pit at the run speed, landing a body length past its far edge.
    const landing = ride.segment.startDistance + pit.zTo + config.pit.landingMargin;
    const physics = player.jumpPhysics;
    player.dismount(catapultSpeed(physics, this.seat.seatHeight, landing - seatDistance, this.deps.speedAt(seatDistance), this.deps.fixedStep));
    events.emit('dozer:pitReached', { ride, filled: false, bottles: 0 });
    this.abandon(ride, DozerExit.Pit, true);
  }

  /** Bottles for the rider – an extra life through score:extraLife like every pickup. */
  private credit(bottles: number): void {
    const { score, events } = this.deps;
    if (score.addBottles(bottles) > 0) events.emit('score:extraLife', { extraLives: score.snapshot().extraLives });
  }

  // ── Abandoned ────────────────────────────────────────────────────────────

  /**
   * The rider is off: the dozer brakes to a stop (at once at the pit edge) – never into an open pit, never beyond
   * the end of its module.
   */
  private abandon(ride: DozerRide, reason: DozerExit, hardStop = false): void {
    const { config, events } = this.deps;
    ride.phase = DozerPhase.Abandoned;
    const z = ride.pose.z;
    const speed = ride.pose.speed;
    let limit = ride.segment.definition.length - this.span.front;
    if (ride.pit && ride.pitFilled !== true && ride.pit.zFrom - config.pit.stopMargin - this.span.front > z - EPSILON) {
      limit = Math.min(limit, ride.pit.zFrom - config.pit.stopMargin - this.span.front);
    }
    const natural = z + (speed * speed) / (2 * config.brakeDeceleration);
    ride.stopZ = hardStop ? z : clamp(natural, z, Math.max(z, limit));
    const room = ride.stopZ - z;
    ride.deceleration = room > EPSILON ? Math.max(config.brakeDeceleration, (speed * speed) / (2 * room)) : Infinity;
    if (room <= EPSILON) ride.pose.speed = 0;
    events.emit('dozer:left', { ride, reason });
  }

  private brake(ride: DozerRide, dt: number): void {
    const pose = ride.pose;
    if (pose.speed <= 0) return;
    pose.speed = Math.max(0, pose.speed - ride.deceleration * dt);
    const z = Math.min(ride.stopZ, pose.z + pose.speed * dt);
    pose.travelled += z - pose.z;
    pose.z = z;
    if (z >= ride.stopZ - EPSILON) pose.speed = 0;
  }
}
